//@ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAlert } from 'react-alert';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import PageTemplate from 'PageTemplates/PageTemplate';
import { PageHeader } from 'components/header';
import { setSpinner } from 'services/store/actions/view';
import {
  getStorageOrderById,
  updateStorageOrder,
  getStorageItems,
} from 'services/apiRequests/storage.api';
import ProductsTable from '../StorageNewOrder/components/ProductsTable';
import OrderSummary from '../StorageNewOrder/components/OrderSummary';
import { validateOrderData } from '../StorageNewOrder/utils/orderUtils';

const StorageOrderEdit = () => {
  const { orderId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const alert = useAlert();

  const [order, setOrder] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [orderItems, setOrderItems] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    dispatch(setSpinner(true));

    try {
      // Pobierz szczegóły zamówienia
      const orderResponse = await getStorageOrderById(orderId);
      const orderData = orderResponse.order;

      if (orderData.status !== 'oczekuje') {
        alert.error(
          'Można edytować tylko zamówienia o statusie "oczekuje"',
        );
        history.push(`/storage/orders/${orderId}`);
        return;
      }

      setOrder(orderData);

      // Pobierz dostępne produkty dla tego producenta
      const itemsResponse = await getStorageItems();
      const filteredProducts = itemsResponse.items.filter((product) =>
        product.producers.some(
          (producer) => producer.name === orderData.producer.name,
        ),
      );

      setAvailableProducts(filteredProducts);

      // Ustaw obecne ilości z zamówienia - dopasuj po nazwie
      const currentItems = {};

      orderData.items.forEach((orderItem) => {
        const itemName = orderItem.item.name || orderItem.name;

        // Znajdź odpowiadający produkt w dostępnych produktach po nazwie
        const matchingProduct = filteredProducts.find(
          (product) => product.name === itemName,
        );

        if (matchingProduct) {
          currentItems[matchingProduct._id] =
            orderItem.quantity.toString();
        }
      });

      // Dla nowych produktów (które nie są w zamówieniu) ustaw 0
      filteredProducts.forEach((product) => {
        if (!currentItems[product._id]) {
          currentItems[product._id] = '0';
        }
      });

      setOrderItems(currentItems);
    } catch (error) {
      console.error('Error fetching order details:', error);
      alert.error('Błąd pobierania danych zamówienia');
      history.push('/storage');
    } finally {
      setLoading(false);
      dispatch(setSpinner(false));
    }
  };

  const handleQuantityChange = (productId, quantity) => {
    setOrderItems((prev) => ({
      ...prev,
      [productId]: quantity.toString(),
    }));
  };

  const handleBackClick = () => {
    history.push(`/storage/orders/${orderId}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Walidacja używając tej samej funkcji co nowe zamówienie
    const validation = validateOrderData(order.producer.name, orderItems);

    if (!validation.isValid) {
      validation.errors.forEach((error) => alert.error(error));
      return;
    }

    try {
      setIsSubmitting(true);
      dispatch(setSpinner(true));

      await updateStorageOrder(orderId, {
        items: validation.itemsToOrder,
      });

      alert.success('Zamówienie zostało zaktualizowane pomyślnie!');
      history.push(`/storage/orders/${orderId}`);
    } catch (error) {
      console.error('Error updating order:', error);
      alert.error('Błąd podczas aktualizacji zamówienia');
    } finally {
      setIsSubmitting(false);
      dispatch(setSpinner(false));
    }
  };

  const calculateTotalQuantity = () => {
    return Object.values(orderItems).reduce(
      (sum, quantity) => sum + (parseInt(quantity, 10) || 0),
      0,
    );
  };

  const calculateTotalPrice = () => {
    return Object.entries(orderItems).reduce(
      (sum, [productId, quantity]) => {
        const product = availableProducts.find((p) => p._id === productId);
        if (product && quantity > 0) {
          return sum + product.price * parseInt(quantity, 10);
        }
        return sum;
      },
      0,
    );
  };

  if (loading) {
    return (
      <PageTemplate>
        <div className="d-flex justify-content-center my-4">
          <div className="spinner-border" role="status">
            <span className="sr-only">Ładowanie...</span>
          </div>
        </div>
      </PageTemplate>
    );
  }

  if (!order) {
    return (
      <PageTemplate>
        <Container>
          <div className="text-center my-4">
            <p>Nie znaleziono zamówienia</p>
            <Button
              variant="primary"
              onClick={() => history.push('/storage')}
            >
              Powrót do magazynu
            </Button>
          </div>
        </Container>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <PageHeader
        title={`Edytuj zamówienie ${order.number}`}
        extra={[
          <Button
            key="back"
            variant="outline-secondary"
            onClick={handleBackClick}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Powrót
          </Button>,
        ]}
      />

      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Edycja zamówienia</h5>
                <small className="text-muted">
                  Producent: <strong>{order.producer.name}</strong> (nie
                  można zmienić)
                </small>
              </Card.Header>
              <Card.Body>
                {availableProducts.length > 0 && (
                  <>
                    <ProductsTable
                      products={availableProducts}
                      orderItems={orderItems}
                      onQuantityChange={handleQuantityChange}
                    />

                    <OrderSummary
                      totalQuantity={calculateTotalQuantity()}
                      totalPrice={calculateTotalPrice()}
                    />

                    <div className="d-grid gap-2 mt-4">
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          variant="outline-secondary"
                          onClick={handleBackClick}
                          disabled={isSubmitting}
                        >
                          Anuluj
                        </Button>
                        <Button
                          variant="success"
                          size="lg"
                          onClick={handleSubmit}
                          disabled={
                            isSubmitting || calculateTotalQuantity() === 0
                          }
                        >
                          {isSubmitting
                            ? 'Zapisywanie zmian...'
                            : 'Zapisz zmiany'}
                        </Button>
                      </div>
                    </div>
                  </>
                )}

                {availableProducts.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-muted">
                      Brak produktów dla producenta:{' '}
                      <strong>{order.producer.name}</strong>
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </PageTemplate>
  );
};

export default StorageOrderEdit;
